import React, { useState, useEffect, useCallback } from "react";
import HubspotForm from "react-hubspot-form";
import GlobalWeatherScreenshot from "../../../bundle-data/images/screenshot.jpg";
import { makeStyles } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  mapHero: {
    backgroundImage: `url(${GlobalWeatherScreenshot})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    height: 601,
    marginTop: 100,

    // ↓ mobile (≤ 479 px)
    [theme.breakpoints.down("xs")]: {
      height: 450,
      marginTop: 20,
    },
  },

  formContainer: {
    padding: "80px 20px",
    maxWidth: "800px",
    margin: "0 auto",
    minHeight: "900px",
    "& .hs-form-iframe": {
      height: "900px !important",
    },
  },

  formHeader: {
    color: "#334155",
    fontSize: "45px",
    fontWeight: "bold",
    lineHeight: "58px",
    marginBottom: "20px",

    // ↓ mobile (≤ 479 px)
    [theme.breakpoints.down("xs")]: {
      fontSize: "35px",
      lineHeight: "110%",
      marginBottom: "10px",
    },
  },

  formText: {
    color: "#334155",
    fontSize: "20px",
    lineHeight: "26px",
  },
}));

const SOFAR_OCEAN_BASE_URL = "https://www.sofarocean.com";

export const FormScreen = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isAdBlocked, setIsAdBlocked] = useState(false);

  const testHubspotConnection = useCallback(async () => {
    // test the connection to hubspot
    try {
      const formScript = await fetch(
        "https://js.hsforms.net/forms/embed/v2.js"
      );
      if (formScript.ok) {
        return;
      }
    } catch (error) {
      // if the request fails, it means that the adblocker is blocking the request
    }
    setIsAdBlocked(true);
  }, []);

  // Pull the public Sofar stylesheet into <head> the first time this mounts
  useEffect(() => {
    const href =
      "https://cdn.prod.website-files.com/64d14310c4accecbf82f0e9f/css/sofar-ocean.3a99e7e0b.css";
    if (!document.querySelector(`link[href='${href}']`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      link.onload = () => {
        setIsLoaded(true); // this prevents a flash of unstyled content
      };
      document.head.appendChild(link);
    }
    testHubspotConnection();
  }, [testHubspotConnection]);

  const classes = useStyles();

  return !isLoaded ? null : ( // wait for the stylesheet to load
    <div style={{ fontFamily: "Figtree, sans-serif" }}>
      <SofarNav />
      <div className={classes.mapHero}></div>
      {isAdBlocked ? (
        <h3
          style={{
            height: "500px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Please turn off your ad blocker or whitelist this site to use the
          weather dashboard.
        </h3>
      ) : (
        <div className={classes.formContainer}>
          <div style={{ marginBottom: "60px" }}>
            <h2 className={classes.formHeader}>
              Access Sofar's Weather Dashboard
            </h2>
            <p className={classes.formText}>
              Our interactive Weather Dashboard showcases the real-time data
              collected by our global, free-drifting network of ocean sensors,
              known as{" "}
              <a
                href={`${SOFAR_OCEAN_BASE_URL}/products/spotter`}
                style={{
                  color: "#334155",
                  textDecoration: "underline",
                  fontWeight: "500",
                }}
              >
                Spotters
              </a>
              .
            </p>
            <br />
            <p className={classes.formText}>
              Please fill out the form below to access the Weather Dashboard.
            </p>
          </div>
          <HubspotForm
            portalId="5311899"
            formId="f80dfafc-b1bb-4b5a-a48b-e7291969d0fb"
            region="na1"
          />
        </div>
      )}
      <SofarFooter />
    </div>
  );
};

/**
 * SofarNav – responsive top‑navigation pulled from www.sofarocean.com (April 2025)
 * ---------------------------------------------------------------------------
 * • Keeps original class names so the remote Webflow stylesheet can skin it.
 * • Injects that stylesheet once on mount – no build‑step needed.
 * • Pure client‑side state replaces the jQuery that toggled the mobile menu.
 * • No props required – links & CTA are hard‑coded exactly as on the site.
 * ---------------------------------------------------------------------------
 * Usage:
 *   import SofarNav from "./SofarNav";
 *   ...
 *   <SofarNav />
 */
function SofarNav() {
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleMobile = () => {
    setMobileOpen((open) => {
      document.body.classList.toggle("sf-no-scroll", !open);
      return !open;
    });
  };

  const navLinks = [
    [`${SOFAR_OCEAN_BASE_URL}/sofar-approach`, "Sofar Approach"],
    [`${SOFAR_OCEAN_BASE_URL}/products/wayfinder`, "Wayfinder"],
    [`${SOFAR_OCEAN_BASE_URL}/products/spotter`, "Spotter Platform"],
    [`${SOFAR_OCEAN_BASE_URL}/updates`, "Updates"],
    [`${SOFAR_OCEAN_BASE_URL}/support`, "Support"],
    [`${SOFAR_OCEAN_BASE_URL}/company/careers`, "Careers"],
  ];

  const contactUsLink = `${SOFAR_OCEAN_BASE_URL}/company/contact-us`;

  return (
    <nav data-wf--sf-navigation--variant="base" className="sf-navigation">
      <style>
        {
          // fix some styles from the Webflow stylesheet
          `
        .line-track {
          width: 0;
          height: 1px;
        }
        .line-hover-button:hover .line-track {
          width: 100%;
          transition: width 0.3s ease-in-out;
        }`
        }
      </style>
      <div className="sf-nav-container">
        {/* Logo */}
        <a
          href={SOFAR_OCEAN_BASE_URL}
          className="sf-nav-logo w-inline-block"
          style={{
            height: "48px",
            minWidth: "160px",
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          <img
            src="https://cdn.prod.website-files.com/62a1247cc21b46074ffbe383/62b5d5545e15ab82d42b7b19_sf-logo-blue.svg"
            alt="Sofar Ocean logo"
            className="sf-nav-logo-src"
            style={{
              height: "100%",
              width: "auto",
              display: "block",
              opacity: 1,
            }}
          />
        </a>

        {/* Primary links */}
        <div className="sf-nav-primary">
          {navLinks.map(([href, label]) => (
            <a
              key={href}
              href={href}
              className="sf-nav-link-block line-hover-button hover-line w-inline-block"
            >
              <div className="sf-nav-text">{label}</div>
              <div className="line-wrap">
                <div className="line-track" />
              </div>
            </a>
          ))}

          <div className="sf-nav-contact-wrap">
            <a href={contactUsLink} className="sf-button-sm-nav w-button">
              Get in Touch
            </a>
          </div>
        </div>

        {/* Mobile hamburger */}
        <div
          className={`ma-fp-btn-toggle-trig ${mobileOpen ? "bang" : ""}`}
          onClick={toggleMobile}
          role="button"
          aria-label="Toggle navigation"
        >
          <div className="ma-btn-toggle">
            <div
              className={`ma-btn-line-top ${mobileOpen ? "active hover" : ""}`}
            />
            <div
              className={`ma-btn-line-bottom ${
                mobileOpen ? "active hover" : ""
              }`}
            />
          </div>
          <div className={`ma-btn-bg ${mobileOpen ? "active" : ""}`} />
        </div>
      </div>

      {/* Mobile slide‑out menu */}
      <div
        className="sf-nav-m"
        style={{ display: mobileOpen ? "block" : "none" }}
      >
        <div className="sf-nav-m-container">
          {navLinks.map(([href, label]) => (
            <a
              key={href}
              href={href}
              className="sf-nav-link-block-light-mobile w-inline-block"
            >
              <div className="sf-mobile-menu-text-dark">{label}</div>
            </a>
          ))}

          <div className="sf-nav-m-secondary">
            <div className="sf-nav-m-secondary-wrap">
              <a href={contactUsLink} className="button w-button">
                Get in Touch
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}

/**
 * SofarFooter – faithful copy of footer from www.sofarocean.com (Apr 2025)
 * ---------------------------------------------------------------------------
 * Keeps original class names, injects the public Webflow stylesheet, and
 * outputs the current year. Social icons now use the exact inner markup from
 * the live site so sizing matches (small 24 px squares instead of giant boxes).
 */
export default function SofarFooter() {
  const year = new Date().getFullYear();

  return (
    <footer className="footer_component">
      <div className="padding-global">
        <div className="container-large">
          <div className="padding-vertical padding-custom1">
            <div className="footer_top-wrapper">
              {/* Primary links */}
              <div className="w-layout-grid footer_link-list">
                {[
                  [`${SOFAR_OCEAN_BASE_URL}/sofar-approach`, "Sofar Approach"],

                  [
                    `${SOFAR_OCEAN_BASE_URL}/company/climate-commitment`,
                    "Climate Commitment",
                  ],
                  [`${SOFAR_OCEAN_BASE_URL}/company/careers`, "Careers"],
                  [
                    "https://spotter.sofarocean.com/",
                    "Spotter Login",
                    "_blank",
                  ],
                  [
                    `${SOFAR_OCEAN_BASE_URL}/mx/sofar-spotter-archive`,
                    "Spotter Archive",
                  ],
                  [
                    `${SOFAR_OCEAN_BASE_URL}/mx/atlantic-hurricane-season-2024`,
                    "Hurricane Tracker",
                  ],
                  [`${SOFAR_OCEAN_BASE_URL}/company/about`, "About"],
                ].map(([href, label, target]) => (
                  <a
                    key={href}
                    href={href}
                    target={target}
                    rel={target ? "noopener noreferrer" : undefined}
                    className="footer_link"
                  >
                    {label}
                  </a>
                ))}
              </div>

              {/* Logo */}
              <a
                href={SOFAR_OCEAN_BASE_URL}
                aria-label="Sofar Home Page"
                className="footer_logo-link w-nav-brand"
              >
                <div
                  className="footer-logo w-embed"
                  style={{ width: 174, height: 49 }}
                >
                  {/* Inline SVG for reliability */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="100%"
                    height="100%"
                    viewBox="0 0 174 49"
                    fill="none"
                    preserveAspectRatio="xMidYMid meet"
                    aria-hidden="true"
                    role="img"
                  >
                    <g clip-path="url(#clip0_296_557)">
                      <path
                        d="M152.207 36.2579L143.325 12.7407H137.169L128.188 36.2579H133.773C134.913 32.8426 138.176 30.3695 142.016 30.3695C142.855 30.3695 143.672 30.4844 144.457 30.7112L146.419 36.2579H152.205H152.207ZM142.016 25.5454C140.378 25.5454 138.806 25.8333 137.351 26.361L140.164 18.5797L142.635 25.5599C142.43 25.5512 142.224 25.5454 142.018 25.5454H142.016Z"
                        fill="#FFFDF9"
                      ></path>
                      <path
                        d="M74.0486 19.8404C73.8805 18.7456 72.9722 16.8889 70.079 16.8889C68.0943 16.8889 66.8158 18.116 66.8158 19.41C66.8158 20.5383 67.5221 21.367 69.1707 21.6651L72.3661 22.2626C76.9078 23.0913 79.2627 25.9774 79.2627 29.3272C79.2627 33.0085 76.1336 36.7567 70.4492 36.7567C63.8563 36.7567 61.0296 32.5447 60.6934 29.2269L65.5713 28.0667C65.7733 30.2228 67.2537 32.1129 70.517 32.1129C72.6035 32.1129 73.8805 31.1184 73.8805 29.6587C73.8805 28.465 72.9043 27.6029 71.3236 27.3034L68.0603 26.7058C64.023 25.9425 61.5678 23.2891 61.5678 19.8069C61.5678 15.3623 65.5373 12.2437 70.113 12.2437C76.0673 12.2437 78.3219 15.7592 78.8262 18.513L74.0486 19.8404Z"
                        fill="#FFFDF9"
                      ></path>
                      <path
                        d="M94.995 12.2437C101.69 12.2437 107.374 17.0532 107.374 24.5162C107.374 31.9792 101.69 36.7553 94.995 36.7553C88.3004 36.7553 82.582 31.9457 82.582 24.5162C82.582 17.0867 88.268 12.2437 94.995 12.2437ZM94.995 31.5808C98.2922 31.5808 101.858 29.3912 101.858 24.4827C101.858 19.5743 98.2922 17.3513 94.995 17.3513C91.6979 17.3513 88.0984 19.5743 88.0984 24.4827C88.0984 29.3912 91.6639 31.5808 94.995 31.5808Z"
                        fill="#FFFDF9"
                      ></path>
                      <path
                        d="M111.792 36.2579V12.7422H127.166V17.7175H117.108V22.9952H125.955V27.7714H117.142V36.2593H111.793L111.792 36.2579Z"
                        fill="#FFFDF9"
                      ></path>
                      <path
                        d="M169.267 26.915L173.999 36.2593H168.145L163.873 27.5024H160.765V36.2593H155.449V12.7422H164.903C169.613 12.7422 172.64 15.9263 172.64 20.1063C172.64 22.892 171.274 25.0947 168.973 26.2898L169.266 26.915H169.267ZM163.894 22.9909C166.081 22.9909 167.258 21.7972 167.258 20.1383C167.258 18.3805 166.081 17.2857 163.894 17.2857H160.765V22.9909H163.894Z"
                        fill="#FFFDF9"
                      ></path>
                      <path
                        d="M36.9172 29.8853L18.5425 36.8408C14.3371 38.4329 9.96494 39.4986 5.51172 40.0205C7.01579 41.8307 8.77791 43.4227 10.7435 44.7458C12.7357 44.2631 14.6998 43.6582 16.6241 42.9298L34.9988 35.9743C39.1645 34.3982 43.5145 33.4808 47.9574 33.238C48.6637 31.4133 49.1562 29.4854 49.4025 27.4819H48.4676C44.5069 27.6753 40.6273 28.4808 36.9172 29.8853Z"
                        fill="#FFFDF9"
                      ></path>
                      <path
                        d="M28.335 27.4819H0.0732422C0.402073 30.147 1.16295 32.6811 2.28806 35.0118H2.36178C7.24411 34.6919 12.0424 33.6495 16.6239 31.9149L28.335 27.4819Z"
                        fill="#FFFDF9"
                      ></path>
                      <path
                        d="M36.9177 40.9001L18.543 47.8556C18.3469 47.9298 18.1478 48.001 17.9502 48.0737C20.1075 48.6771 22.3842 48.9998 24.7377 48.9998C33.0292 48.9998 40.3711 44.9943 44.8848 38.8413C42.1672 39.2353 39.5011 39.923 36.9177 40.9015V40.9001Z"
                        fill="#FFFDF9"
                      ></path>
                      <path
                        d="M48.3514 22.2057C48.7244 22.1883 49.099 22.1766 49.4735 22.1679C48.282 9.73108 37.6636 0 24.739 0C11.8143 0 1.17376 9.74998 0 22.2057H48.3514Z"
                        fill="#FFFDF9"
                      ></path>
                    </g>
                    <defs>
                      <clipPath id="clip0_296_557">
                        <rect width="174" height="49" fill="white"></rect>
                      </clipPath>
                    </defs>
                  </svg>{" "}
                </div>
              </a>

              {/* Social icons – original markup to inherit size */}
              <div className="w-layout-grid footer_legal-list">
                <a
                  aria-label="LinkedIn"
                  href="https://www.linkedin.com/company/sofar-ocean"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sf-footer-social w-inline-block"
                >
                  <div className="icon-1x1-small w-embed">
                    <svg
                      viewBox="0 0 24 24"
                      width="100%"
                      height="100%"
                      fill="currentColor"
                    >
                      <path d="M20 3H4a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1zM8.34 18.34H5.67v-8.59h2.67v8.59zM7 8.57a1.55 1.55 0 1 1 0-3.1 1.55 1.55 0 0 1 0 3.1zm11.34 9.77h-2.67V14.16c0-.996-.02-2.28-1.39-2.28-1.39 0-1.6 1.09-1.6 2.21v4.25h-2.67v-8.59h2.56v1.17h.04c.36-.67 1.23-1.39 2.52-1.39 2.7 0 3.2 1.78 3.2 4.09v4.71z" />
                    </svg>
                  </div>
                </a>
                <a
                  aria-label="YouTube"
                  href="https://www.youtube.com/c/Sofar_ocean"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sf-footer-social w-inline-block"
                >
                  <div className="icon-1x1-small w-embed">
                    <svg
                      viewBox="0 0 24 24"
                      width="100%"
                      height="100%"
                      fill="currentColor"
                    >
                      <path d="M21.593 7.203a2.506 2.506 0 0 0-1.762-1.766C18.265 5.007 12 5 12 5s-6.264-.007-7.831.404a2.56 2.56 0 0 0-1.766 1.778c-.413 1.566-.417 4.814-.417 4.814s-.004 3.264.406 4.814c.23.857.905 1.534 1.763 1.765 1.582.43 7.83.437 7.83.437s6.265.007 7.831-.403a2.515 2.515 0 0 0 1.767-1.763c.414-1.565.417-4.812.417-4.812s.02-3.265-.407-4.831zM9.996 15.005l.005-6 5.207 3.005-5.212 2.995z" />
                    </svg>
                  </div>
                </a>
                <a
                  aria-label="Instagram"
                  href="https://www.instagram.com/sofarocean/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sf-footer-social w-inline-block"
                >
                  <div className="icon-1x1-small w-embed">
                    <svg
                      aria-hidden="true"
                      role="img"
                      width="100%"
                      height="100%"
                      preserveAspectRatio="xMidYMid meet"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M11.999 7.377a4.623 4.623 0 1 0 0 9.248a4.623 4.623 0 0 0 0-9.248zm0 7.627a3.004 3.004 0 1 1 0-6.008a3.004 3.004 0 0 1 0 6.008z"
                        fill="currentColor"
                      ></path>
                      <circle
                        cx="16.806"
                        cy="7.207"
                        r="1.078"
                        fill="currentColor"
                      ></circle>
                      <path
                        d="M20.533 6.111A4.605 4.605 0 0 0 17.9 3.479a6.606 6.606 0 0 0-2.186-.42c-.963-.042-1.268-.054-3.71-.054s-2.755 0-3.71.054a6.554 6.554 0 0 0-2.184.42a4.6 4.6 0 0 0-2.633 2.632a6.585 6.585 0 0 0-.419 2.186c-.043.962-.056 1.267-.056 3.71c0 2.442 0 2.753.056 3.71c.015.748.156 1.486.419 2.187a4.61 4.61 0 0 0 2.634 2.632a6.584 6.584 0 0 0 2.185.45c.963.042 1.268.055 3.71.055s2.755 0 3.71-.055a6.615 6.615 0 0 0 2.186-.419a4.613 4.613 0 0 0 2.633-2.633c.263-.7.404-1.438.419-2.186c.043-.962.056-1.267.056-3.71s0-2.753-.056-3.71a6.581 6.581 0 0 0-.421-2.217zm-1.218 9.532a5.043 5.043 0 0 1-.311 1.688a2.987 2.987 0 0 1-1.712 1.711a4.985 4.985 0 0 1-1.67.311c-.95.044-1.218.055-3.654.055c-2.438 0-2.687 0-3.655-.055a4.96 4.96 0 0 1-1.669-.311a2.985 2.985 0 0 1-1.719-1.711a5.08 5.08 0 0 1-.311-1.669c-.043-.95-.053-1.218-.053-3.654c0-2.437 0-2.686.053-3.655a5.038 5.038 0 0 1 .311-1.687c.305-.789.93-1.41 1.719-1.712a5.01 5.01 0 0 1 1.669-.311c.951-.043 1.218-.055 3.655-.055s2.687 0 3.654.055a4.96 4.96 0 0 1 1.67.311a2.991 2.991 0 0 1 1.712 1.712a5.08 5.08 0 0 1 .311 1.669c.043.951.054 1.218.054 3.655c0 2.436 0 2.698-.043 3.654h-.011z"
                        fill="currentColor"
                      ></path>
                    </svg>
                  </div>
                </a>
              </div>

              {/* Legal */}
              <div className="w-layout-grid footer_legal-list is-mobile-flex">
                <div className="footer_legal-link is-copyright">
                  ©{year} Sofar Ocean
                </div>
                <div className="legal-link_wrapper">
                  <a
                    href={`${SOFAR_OCEAN_BASE_URL}/legal`}
                    className="footer_legal-link"
                  >
                    Legal
                  </a>
                  <div className="circle-spacer" />
                  <a
                    href={`${SOFAR_OCEAN_BASE_URL}/legal/privacy-policy`}
                    className="footer_legal-link"
                  >
                    Privacy Policy
                  </a>
                  <div className="circle-spacer hide-mobile-landscape" />
                  <a
                    href={`${SOFAR_OCEAN_BASE_URL}/legal/terms-of-use`}
                    className="footer_legal-link"
                  >
                    Terms of Use
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
